import api from '../../api/restaurant/restaurantPurchaseOrderApi';
import {
  CustomResponse,
  RestaurantPurchaseOrderDTO,
  RestaurantPurchaseOrderFilters,
  RestaurantPurchaseOrderForm,
  RestaurantPurchaseOrderPage,
} from '../../types/restaurant/RestaurantPurchaseOrderTypes';
import { getRestaurantPurchasePaymentError } from '../../features/restaurant/purchases/restaurantPurchasePayment';
import { appendRestaurantQuery } from './restaurantPageQuery';

export const restaurantPurchaseOrderPayload = (
  form: RestaurantPurchaseOrderForm
) => {
  const paymentError = getRestaurantPurchasePaymentError(
    form.purchaseDate,
    form.paymentCondition
  );
  if (paymentError) throw new Error(paymentError);

  // La validación anterior garantiza la presencia de la condición.
  const paymentCondition = form.paymentCondition!;
  const isCredit = paymentCondition.code === 'CREDITO';
  const termCode = isCredit ? paymentCondition.termCode : null;
  const amount =
    termCode === '01'
      ? paymentCondition.creditDays
      : termCode === '02'
      ? paymentCondition.creditMonths
      : termCode === '03'
      ? paymentCondition.creditYears
      : null;

  return {
    supplierId: Number(form.supplierId),
    purchaseDate: form.purchaseDate,
    paymentCondition: {
      id: paymentCondition.id,
      name: paymentCondition.name,
      code: paymentCondition.code,
      creditDueDate: isCredit ? paymentCondition.creditDueDate : null,
      creditDays: termCode === '01' ? amount : null,
      creditMonths: termCode === '02' ? amount : null,
      creditYears: termCode === '03' ? amount : null,
      termId: isCredit ? paymentCondition.termId : null,
      termName: isCredit ? paymentCondition.termName : null,
      termCode,
    },
    notes: form.notes.trim() || null,
    dte: form.dte
      ? {
          type: form.dte.type || null,
          controlNumber: form.dte.controlNumber || null,
          generationCode: form.dte.generationCode || null,
          taxedTotal: Number(form.dte.taxedTotal || 0),
          exemptTotal: Number(form.dte.exemptTotal || 0),
          nonSubjectTotal: Number(form.dte.nonSubjectTotal || 0),
          discountTotal: Number(form.dte.discountTotal || 0),
          ivaTotal: Number(form.dte.ivaTotal || 0),
          ivaPerception: Number(form.dte.ivaPerception || 0),
          ivaRetention: Number(form.dte.ivaRetention || 0),
          incomeRetention: Number(form.dte.incomeRetention || 0),
          operationTotal: Number(form.dte.operationTotal || 0),
          payableTotal: Number(form.dte.payableTotal || 0),
        }
      : null,
    details: form.details.map((detail) => ({
      rawMaterialId: Number(detail.rawMaterialId),
      quantity: Number(detail.quantity),
      unitId: Number(detail.unitId),
      unitCost: Number(detail.unitCost),
      discountAmount: Number(detail.discountAmount || 0),
      taxAmount: Number(detail.taxAmount || 0),
      dteTaxedSale: Number(detail.dteTaxedSale || 0),
      dteExemptSale: Number(detail.dteExemptSale || 0),
      dteNonSubjectSale: Number(detail.dteNonSubjectSale || 0),
      dteDiscountAmount: Number(detail.dteDiscountAmount || 0),
      expirationDate: detail.expirationDate || null,
      lotNumber: detail.lotNumber.trim() || null,
    })),
  };
};

const RestaurantPurchaseOrderService = {
  list: async (
    branchId: number,
    filters: RestaurantPurchaseOrderFilters = {}
  ) => {
    const query = appendRestaurantQuery(
      new URLSearchParams({
        page: String(filters.page ?? 0),
        size: String(filters.size ?? 20),
      }),
      {
        supplierId: filters.supplierId,
        status: filters.status,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        search: filters.search?.trim(),
      }
    );
    return (await api.fetchCustom(`/branches/${branchId}?${query}`))
      .data as RestaurantPurchaseOrderPage;
  },
  get: async (id: number) =>
    (await api.fetchById(id))
      .data as CustomResponse<RestaurantPurchaseOrderDTO>,
  create: async (branchId: number, form: RestaurantPurchaseOrderForm) =>
    (await api.addCustom(
      `/branches/${branchId}`,
      restaurantPurchaseOrderPayload(form)
    ))
      .data as CustomResponse<RestaurantPurchaseOrderDTO>,
  update: async (id: number, form: RestaurantPurchaseOrderForm) =>
    (await api.modify(
      id,
      restaurantPurchaseOrderPayload(form)
    ))
      .data as CustomResponse<RestaurantPurchaseOrderDTO>,
  confirm: async (id: number) =>
    (await api.addWithoutData(`/${id}/confirm`))
      .data as CustomResponse<RestaurantPurchaseOrderDTO>,
  cancel: async (id: number) =>
    (await api.addWithoutData(`/${id}/cancel`))
      .data as CustomResponse<RestaurantPurchaseOrderDTO>,
};

export default RestaurantPurchaseOrderService;
